import {
  ArrowRight,
  Bot,
  Check,
  CircleDot,
  Database,
  FileText,
  Gift,
  Lightbulb,
  Mail,
  Network,
  Sparkles,
  Star,
  Workflow,
} from 'lucide-react';
import type { Locale } from './Navigation';

const labels = {
  sv: {
    idea: {
      notes: ['Lös idé', 'Tidsvinst', 'Överblick'],
      insight: 'Insikt',
      structure: 'Struktur',
      result: 'Tydlig riktning',
      chip: 'Idé → produkt',
      journey: ['Idé', 'Insikt', 'Struktur', 'Lösning'],
    },
    problem: {
      items: ['E-post', 'Formulär', 'Anteckning', 'System'],
      collect: 'Samla',
      steps: ['Förstå', 'Samla', 'Förslag', 'Nästa steg'],
      chip: 'Från friktion till flöde',
      outcome: 'Tydlig process · praktisk lösning',
    },
    product: ['Idé', 'Prototyp', 'Bygg', 'Förfina'],
    productChip: 'Produktutveckling',
    digitization: {
      manual: ['E-post', 'Excel', 'Formulär', 'Anteckning'],
      nav: ['Översikt', 'Flöden', 'Data', 'Rapporter'],
      flow: ['Begäran', 'Godkänn', 'Utför', 'Följ upp'],
      chip: 'Digitalisera · samla · förenkla',
    },
    automation: {
      inputs: ['Formulär', 'E-post', 'Dokument', 'Databas'],
      assist: 'AI-assistans',
      cards: ['Sammanfatta', 'Klassificera', 'Föreslå'],
      triggers: ['Villkor', 'Automatik', 'Uppdatera'],
      actions: ['Skapa', 'Meddela', 'Uppdatera'],
      chip: 'AI + automation',
    },
    fampo: {
      balance: 'Poängsaldo',
      tasks: ['Bädda sängen', 'Duka bordet', 'Läxor'],
      rewards: ['Filmkväll', 'Glass', 'Välj aktivitet'],
      completed: 'Uppgift klar',
      family: 'Familjen',
    },
    association: {
      nav: ['Översikt', 'Medlemmar', 'Utskick', 'Dokument'],
      modules: ['Medlemmar', 'Ekonomi', 'Kommunikation'],
      activity: 'Senaste aktivitet',
      status: 'Aktiv utveckling',
    },
  },
  en: {
    idea: {
      notes: ['Rough idea', 'Save time', 'Overview'],
      insight: 'Insight',
      structure: 'Structure',
      result: 'Clear direction',
      chip: 'Idea → product',
      journey: ['Idea', 'Insight', 'Structure', 'Solution'],
    },
    problem: {
      items: ['Email', 'Form', 'Notes', 'System'],
      collect: 'Collect',
      steps: ['Understand', 'Gather', 'Propose', 'Next step'],
      chip: 'From friction to flow',
      outcome: 'Clear process · practical solution',
    },
    product: ['Idea', 'Prototype', 'Build', 'Refine'],
    productChip: 'Product development',
    digitization: {
      manual: ['Email', 'Spreadsheet', 'Form', 'Notes'],
      nav: ['Overview', 'Flows', 'Data', 'Reports'],
      flow: ['Request', 'Approve', 'Execute', 'Follow up'],
      chip: 'Digitize · connect · simplify',
    },
    automation: {
      inputs: ['Forms', 'Email', 'Documents', 'Database'],
      assist: 'AI assist',
      cards: ['Summarize', 'Classify', 'Suggest'],
      triggers: ['Condition', 'Automation', 'Update'],
      actions: ['Create', 'Notify', 'Update'],
      chip: 'AI + automation',
    },
    fampo: {
      balance: 'Point balance',
      tasks: ['Make the bed', 'Set the table', 'Homework'],
      rewards: ['Movie night', 'Ice cream', 'Choose activity'],
      completed: 'Task complete',
      family: 'Family',
    },
    association: {
      nav: ['Overview', 'Members', 'Broadcasts', 'Documents'],
      modules: ['Members', 'Finance', 'Communication'],
      activity: 'Recent activity',
      status: 'Active development',
    },
  },
} as const;

export function IdeaJourneyVisual({ locale }: { locale: Locale }) {
  const l = labels[locale].idea;
  return (
    <div className="idea-visual visual-shell" aria-hidden="true">
      <div className="visual-grid" />
      <div className="visual-aurora visual-aurora--mint" />
      <div className="visual-top-chip"><Sparkles size={13}/><span>{l.chip}</span></div>
      <div className="idea-notes">
        {l.notes.map((note, i) => <div key={note} className={`idea-note idea-note--${i + 1}`}>{note}</div>)}
      </div>
      <div className="idea-route"><span /><span /><span /><span /></div>
      <div className="idea-bulb"><Lightbulb size={38} /><small>{l.insight}</small></div>
      <div className="wireframe-card">
        <div className="window-dots"><i /><i /><i /></div>
        <div className="wireframe-hero" />
        <div className="wireframe-lines"><i /><i /><i /></div>
        <div className="wireframe-tiles"><i /><i /><i /></div>
        <small>{l.structure}</small>
      </div>
      <div className="result-card">
        <div className="result-card__top"><Sparkles size={18} /><span>{l.result}</span></div>
        <div className="result-card__hero" />
        <div className="result-card__metrics"><i /><i /><i /></div>
      </div>
      <div className="visual-journey">{l.journey.map((item,i)=><div key={item} className={i===3?'active':''}><span>{i<3?String(i+1).padStart(2,'0'):'✓'}</span><small>{item}</small></div>)}</div>
    </div>
  );
}

export function ProblemFlowVisual({ locale }: { locale: Locale }) {
  const l = labels[locale].problem;
  const icons = [Mail, FileText, CircleDot, Database];
  return (
    <div className="problem-visual visual-shell" aria-hidden="true">
      <div className="visual-grid" />
      <div className="visual-top-chip"><Workflow size={13}/><span>{l.chip}</span></div>
      <div className="problem-scatter">
        {l.items.map((item, i) => {
          const Icon = icons[i];
          return <div key={item} className={`scatter-card scatter-card--${i + 1}`}><Icon size={16} /><span>{item}</span><i /></div>;
        })}
        <svg className="scatter-lines" viewBox="0 0 420 320"><path d="M45 55 C120 20 120 145 200 100 S290 40 360 95"/><path d="M30 220 C125 170 145 280 230 215 S320 155 385 225"/></svg>
      </div>
      <div className="problem-arrow"><ArrowRight size={24} /></div>
      <div className="problem-stack">
        <span>{l.collect}</span>
        <div><Mail size={15} /><i /></div><div><FileText size={15} /><i /></div><div><Database size={15} /><i /></div>
      </div>
      <div className="problem-steps">
        {l.steps.map((step, i) => <div key={step}><b>{String(i + 1).padStart(2,'0')}</b><span>{step}</span><Check size={15} /></div>)}
      </div>
      <div className="problem-outcome"><Sparkles size={14}/><span>{l.outcome}</span></div>
    </div>
  );
}

export function ProductDevelopmentVisual({ locale }: { locale: Locale }) {
  const l = labels[locale].product;
  return (
    <div className="service-visual service-visual--product" aria-hidden="true">
      <div className="visual-grid" />
      <div className="visual-top-chip"><Sparkles size={13}/><span>{labels[locale].productChip}</span></div>
      <div className="pd-stage pd-stage--wire"><small>01 · {l[0]}</small><div className="sketch-x"/><i/><i/><div className="sketch-chart"/></div>
      <ArrowRight className="pd-arrow pd-arrow--one" size={20}/>
      <div className="pd-phone"><small>02 · {l[1]}</small><div className="pd-phone__hero"/><div className="pd-phone__bars"><i/><i/><i/></div></div>
      <ArrowRight className="pd-arrow pd-arrow--two" size={20}/>
      <div className="pd-dashboard"><small>03 · {l[2]}</small><div className="pd-dashboard__side"><i/><i/><i/><i/></div><div className="pd-dashboard__main"><div className="pd-metrics"><i/><i/><i/></div><div className="pd-chart"><span/><span/><span/><span/><span/></div></div></div>
      <div className="pd-refine"><Star size={20}/><small>04 · {l[3]}</small><div className="pd-refine__orb"/><div className="pd-refine__progress"><i/></div></div>
    </div>
  );
}

export function DigitizationVisual({ locale }: { locale: Locale }) {
  const l = labels[locale].digitization;
  const icons = [Mail, FileText, FileText, CircleDot];
  return (
    <div className="service-visual service-visual--digitize" aria-hidden="true">
      <div className="visual-top-chip"><Database size={13}/><span>{l.chip}</span></div>
      <div className="digitize-manual">
        {l.manual.map((item, i) => { const Icon = icons[i]; return <div key={item} className={`manual-card manual-card--${i+1}`}><Icon size={15}/><span>{item}</span><i/><i/></div>; })}
        <svg viewBox="0 0 360 260"><path d="M30 65 C95 15 120 150 185 90 S270 35 330 95"/><path d="M40 200 C115 145 160 260 225 195 S285 150 330 205"/></svg>
      </div>
      <div className="digitize-arrow"><ArrowRight size={24}/></div>
      <div className="system-window">
        <aside>{l.nav.map((item,i)=><span key={item} className={i===0?'active':''}><i/>{item}</span>)}</aside>
        <main>
          <div className="system-kpis"><i/><i/><i/></div>
          <div className="system-flow">{l.flow.map((item,i)=><div key={item}><b>{i+1}</b><span>{item}</span>{i<l.flow.length-1&&<ArrowRight size={13}/>}</div>)}</div>
          <div className="system-table"><span/><span/><span/><span/></div>
        </main>
      </div>
    </div>
  );
}

export function AutomationVisual({ locale }: { locale: Locale }) {
  const l = labels[locale].automation;
  return (
    <div className="service-visual service-visual--automation" aria-hidden="true">
      <div className="visual-top-chip"><Sparkles size={13}/><span>{l.chip}</span></div>
      <div className="automation-inputs">{l.inputs.map((item,i)=><div key={item}><span>{item}</span><i className={`input-icon input-icon--${i}`}/></div>)}</div>
      <div className="automation-lines"><span className="line-a"/><span className="line-b"/><span className="line-c"/></div>
      <div className="ai-core"><div className="ai-core__head"><Bot size={22}/><strong>{l.assist}</strong></div>{l.cards.map(item=><div key={item} className="ai-core__row"><Sparkles size={14}/><span>{item}</span></div>)}</div>
      <div className="trigger-stack">{l.triggers.map((item,i)=><div key={item}><Network size={15}/><span>{item}</span><b>{i+1}</b></div>)}</div>
      <div className="action-strip">{l.actions.map((item,i)=><div key={item}><Check size={14}/><span>{item}</span>{i<l.actions.length-1&&<ArrowRight size={13}/>}</div>)}</div>
    </div>
  );
}

export function FampoProductVisual({ locale }: { locale: Locale }) {
  const l = labels[locale].fampo;
  return (
    <div className="product-art product-art--fampo-v2" aria-hidden="true">
      <div className="visual-grid"/><div className="visual-aurora visual-aurora--mint"/><div className="visual-aurora visual-aurora--purple"/>
      <div className="fampo-phone-v2">
        <div className="phone-island"/>
        <header><strong>FamPo</strong><span>FP</span></header>
        <div className="fampo-family-line"><span>{l.family}</span><i/><i/><i/></div>
        <div className="fampo-balance"><small>{l.balance}</small><strong>240 <em>FP</em></strong><div className="balance-progress"><i/></div></div>
        <div className="fampo-tasklist">{l.tasks.map((task,i)=><div key={task}><Check size={14}/><span>{task}</span><b>+{[20,30,40][i]} FP</b></div>)}</div>
        <div className="fampo-nav"><i/><i/><i className="active"/><i/></div>
      </div>
      <div className="reward-stack-v2">{l.rewards.map((reward,i)=><div key={reward} className={`reward-card-v2 reward-card-v2--${i+1}`}><Gift size={18}/><span>{reward}</span><b>{[300,150,500][i]} FP</b></div>)}</div>
      <div className="completion-chip"><Check size={15}/><span>{l.completed}</span></div>
    </div>
  );
}

export function AssociationProductVisual({ locale }: { locale: Locale }) {
  const l = labels[locale].association;
  return (
    <div className="product-art product-art--association-v2" aria-hidden="true">
      <div className="visual-grid"/><div className="visual-aurora visual-aurora--purple"/>
      <div className="association-window-v2">
        <header><div className="window-dots"><i/><i/><i/></div><strong>Din Förening</strong></header>
        <div className="association-body-v2">
          <aside>{l.nav.map((item,i)=><span key={item} className={i===0?'active':''}><i/>{item}</span>)}</aside>
          <main>
            <div className="association-module-row">{l.modules.map(item=><div key={item}><small>{item}</small><i/></div>)}</div>
            <div className="association-chart-v2"><span/><span/><span/><span/><span/><span/></div>
            <div className="association-activity-v2"><small>{l.activity}</small><i/><i/><i/></div>
          </main>
        </div>
      </div>
      <div className="association-float-card"><Workflow size={17}/><span>{l.nav[2]}</span></div>
      <div className="association-status-chip"><CircleDot size={14}/><span>{l.status}</span></div>
    </div>
  );
}
